import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModal } from '../../actions';
import {
  setSidebarActiveTab,
  setSidebarOpen,
  setSecondSidebarOpen,
} from '../../actions/App';
import {
  clearFilesList,
  clearFilesFilters,
  clearSelectedFilters,
  setCollectionUuid,
  setFilesLoading,
  setFilesFilters,
  setSelectedFilters,
} from '../../actions/Files';
import {
  setSearchTerm,
  setSearchFields,
  setSearchQueryType,
  fetchExternalFiles,
  fetchExternalCollections,
  refreshExternalSearch,
} from '../../actions/Search';
import {
  clearCollectionsList,
  setCollectionFilters,
  clearCollectionFilters,
  setCollectionsLoading,
  getCollection,
} from '../../actions/Collections';

import { APIgetPageCollectionDetail, cancelPageSearchRequests } from '../../api/Pages';

import { usePageBasePath, usePageRouteId } from '../../helpers/PageIdContext';
import { getPageFileRoute, canDownloadPageItem } from '../../helpers/PageSections';
import {
  filterParams,
  handleComponentParams,
  handleURLParamsRemove,
  collectionFilterParams,
} from '../../helpers/URLParams';

import SidebarFilters from '../../components/Sidebar/Filters';
import GoBackToListButton from '../../components/GoBackToListButton';
import {
  renderPageCollectionAction,
  renderPageFileAction,
} from '../../helpers/PageElementActions';
import CollectionResultsSection from './CollectionResultsSection';
import FileResultsSection from './FileResultsSection';
import Loading from '../../components/Loading';
import EmptySearch from '../EmptyContainers/Search';
import FilterBubbles from '../../components/Filters/FilterBubbles';
import Actionbar from '../../components/Actionbar';

const searchEntities = {
  'custom-page': {
    getBaseUrl: ({ pageBasePath, pageRouteId }) => `/${pageBasePath}/${pageRouteId}`,
    getFileRoute: getPageFileRoute,
  },
};

const ExternalSearchResults = ({
  entityType,
  entityId,
  dispatch,
  filesTotal,
  filesLoading,
  selectedFiles,
  collectionsTotal,
  collectionsLoading,
  selectedCollections,
  sidebarActiveTab,
  selectedFilters,
  pageHeader,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const pageBasePath = usePageBasePath();
  const pageRouteId = usePageRouteId();

  const entity = searchEntities[entityType];

  const routeContext = { pageBasePath, pageRouteId, pageId: entityId };
  const goBackUrl = entity ? entity.getBaseUrl(routeContext) : location.pathname;
  const searchCollectionUuid = location.pathname
    .match(/\/search\/collections\/([^/]+)\/?$/)?.[1] || null;
  const [collectionName, setCollectionName] = useState(null);

  const resetSearchState = useCallback(() => {
    dispatch(clearFilesFilters());
    dispatch(clearCollectionFilters());
    dispatch(clearSelectedFilters());
    dispatch(setCollectionUuid(null));
    dispatch(setSearchTerm(''));
    dispatch(setSearchFields([]));
    dispatch(setSearchQueryType(null));
    dispatch(getCollection(null));
  }, [dispatch]);

  useEffect(() => {
    setCollectionName(null);
    if (!searchCollectionUuid || entityType !== 'custom-page') return undefined;

    let cancelled = false;
    APIgetPageCollectionDetail(entityId, searchCollectionUuid)
      .then(({ data }) => {
        const name = (data?.collection || data?.data?.collection)?.name;
        if (!cancelled && name) setCollectionName(name);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [entityType, entityId, searchCollectionUuid]);

  const fileRouteResolver = useCallback((file) => {
    if (!entity?.getFileRoute) return null;
    return entity.getFileRoute({
      pageBasePath,
      pageRouteId,
      pageId: entityId,
      file,
    });
  }, [entity, pageBasePath, pageRouteId, entityId]);
  const collectionUrlResolver = useCallback((collection) => {
    if (!collection?.uuid) return null;

    const view = new URLSearchParams(location.search).get('view');

    return {
      pathname: `${goBackUrl}/search/collections/${collection.uuid}`,
      search: view ? `?view=${encodeURIComponent(view)}` : '',
    };
  }, [goBackUrl, location.search]);

  const handleRemoveFilter = useCallback((type, key) => {
    const remainingFilters = selectedFilters.filter(filter => (
      filter.type !== 'search_fields' &&
      !(filter.type === type && String(filter.key) === String(key))
    ));

    if (remainingFilters.length === 0 && !searchCollectionUuid) {
      resetSearchState();
      history.push(goBackUrl);
      return;
    }

    if (
      type === 'collections' &&
      /\/search\/collections\/[^/]+\/?$/.test(location.pathname)
    ) {
      history.push({ pathname: `${goBackUrl}/search`, search: location.search });
      return;
    }

    handleURLParamsRemove(type, key, history);
  }, [
    goBackUrl,
    history,
    location.pathname,
    location.search,
    selectedFilters,
    resetSearchState,
    searchCollectionUuid,
  ]);

  useEffect(() => {
    dispatch(setSidebarActiveTab('2'));
    dispatch(setSidebarOpen(true));
    dispatch(setSecondSidebarOpen(true));
  }, [dispatch]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    handleComponentParams(true, filterParams, null, (getParams) => {
      const queryFilters = getParams();
      if (searchCollectionUuid) queryFilters.collections = [];
      dispatch(setFilesFilters(queryFilters));
      dispatch(setCollectionFilters(window._.pick(queryFilters, collectionFilterParams)));
      dispatch(setCollectionUuid(searchCollectionUuid));
      dispatch(setSelectedFilters(Object.entries(queryFilters).flatMap(([type, value]) => (
        (Array.isArray(value) ? value : [value]).filter(Boolean).map(key => ({
          type, key, label: type === 'collections' ? key : undefined,
        }))
      ))));
    });
    dispatch(setSearchTerm(query.get('search')?.trim() || ''));
    dispatch(setSearchFields(query.get('search_fields')?.split(',') || []));
    dispatch(setSearchQueryType(query.get('query_type') || null));

    dispatch(refreshExternalSearch(entityType, entityId));
  }, [dispatch, location.pathname, location.search, entityType, entityId, searchCollectionUuid]);

  useEffect(() => () => {
    cancelPageSearchRequests();
    dispatch(clearFilesList());
    dispatch(setCollectionUuid(null));
    dispatch(clearCollectionsList());
    dispatch(setFilesLoading(false));
    dispatch(setCollectionsLoading(false));
    dispatch(setSearchTerm(''));
    dispatch(setSearchFields([]));
    dispatch(setSearchQueryType(null));
    dispatch(clearFilesFilters());
    dispatch(clearCollectionFilters());
    dispatch(clearSelectedFilters());
    dispatch(setSidebarActiveTab('1'));
    dispatch(setSidebarOpen(false));
    dispatch(setSecondSidebarOpen(false));
  }, [dispatch]);

  const loadMoreFiles = useCallback(
    () => dispatch(fetchExternalFiles(entityType, entityId)),
    [dispatch, entityType, entityId],
  );

  const loadMoreCollections = useCallback(
    () => dispatch(fetchExternalCollections(entityType, entityId)),
    [dispatch, entityType, entityId],
  );

  const refreshSearch = useCallback(
    () => dispatch(refreshExternalSearch(entityType, entityId)),
    [dispatch, entityType, entityId],
  );

  const onShowModal = useCallback(
    (modalName, modalProps) => dispatch(showModal(modalName, modalProps)),
    [dispatch],
  );

  const renderFileDisplayAction = useCallback((file, actionView) => (
    renderPageFileAction({
      actionFile: file,
      actionView,
      displayFile: file,
      allowDownload: canDownloadPageItem(file),
      pageId: entityId,
      onShowModal,
      intl,
    })
  ), [entityId, intl, onShowModal]);

  const renderCollectionDisplayAction = useCallback(collection => (
    renderPageCollectionAction({
      collection,
      pageId: entityId,
      onShowModal,
      intl,
    })
  ), [entityId, intl, onShowModal]);

  return (
    <div className="external-search-modal">
      <div className="external-search-modal-scroll">
        {pageHeader}
        <div className="external-search-results">
          <div className={classnames('main-content', 'split-content', 'page-files')}>
            <div className="split-content-wrapper search-results landing-content">
              <Container className="external-search-results-container">
                {sidebarActiveTab === '2' && (
                  <aside className="file-sidebar">
                    <SidebarFilters type={null} externalSearch />
                  </aside>
                )}
                <div className={
                  classnames('external-search-results-content', { 'external-search-results-content-actionbar': selectedFiles.length > 0 || selectedCollections.length > 0 })
                }
                >
                  <GoBackToListButton
                    goBackUrl={goBackUrl}
                    goBackText={intl.formatMessage({ defaultMessage: 'Back to Content Portal' })}
                    onClick={resetSearchState}
                  />
                  <header className="page-header with-upgrade-button">
                    <h2 className="page-title">
                      {collectionName || intl.formatMessage({ defaultMessage: 'Content Portal search results' })}
                    </h2>
                  </header>
                  {selectedFilters.length > 0 && (
                    <div className="active-filters tags-list">
                      <FilterBubbles
                        filters={selectedFilters.filter(filter => filter.type !== 'search_fields')}
                        onRemoveFilter={handleRemoveFilter}
                      />
                    </div>
                  )}
                  {collectionsTotal > 0 && (
                    <CollectionResultsSection
                      handleMoveSelectedItems={() => { }}
                      onLoadMore={loadMoreCollections}
                      onRefresh={refreshSearch}
                      hideSaveSort={true}
                      displayOnly={true}
                      renderDisplayAction={renderCollectionDisplayAction}
                      collectionUrlResolver={collectionUrlResolver}
                    />
                  )}
                  {filesTotal > 0 && (
                    <FileResultsSection
                      handleMoveSelectedItems={() => { }}
                      onLoadMore={loadMoreFiles}
                      onRefresh={refreshSearch}
                      hideSaveSort={true}
                      displayOnly={true}
                      renderDisplayAction={renderFileDisplayAction}
                      canDrag={false}
                      fileRouteResolver={fileRouteResolver}
                    />
                  )}
                  {(filesLoading || collectionsLoading) && <Loading />}
                  {!(collectionsLoading || filesLoading) &&
                    (filesTotal === 0 && collectionsTotal === 0) && (
                    <EmptySearch />
                  )}
                  <Actionbar />
                </div>
              </Container>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  filesLoading: state.Files.loading,
  selectedFiles: state.Files.selectedFiles,
  collections: state.Collections.collections,
  collectionsTotal: state.Collections.collectionsTotal,
  collectionsLoading: state.Collections.collectionsLoading,
  selectedCollections: state.Collections.selectedCollections,
  sidebarActiveTab: state.App.sidebarActiveTab,
  selectedFilters: state.Files.selectedFilters,
});

ExternalSearchResults.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  filesTotal: PropTypes.number.isRequired,
  filesLoading: PropTypes.bool.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  sidebarActiveTab: PropTypes.string.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  pageHeader: PropTypes.node,
};

ExternalSearchResults.defaultProps = {
  pageHeader: null,
};

export default connect(mapStateToProps)(ExternalSearchResults);
